import React, { useCallback, useEffect, useState } from 'react';
import { Container } from 'react-bootstrap';
import { Editor } from '@tinymce/tinymce-react';
import { Formik } from 'formik';
import {
  FiArrowLeft,
  FiCalendar,
  FiCheckCircle,
  FiFileText,
  FiImage,
  FiInfo,
  FiMic,
  FiSave,
  FiSend,
  FiTag,
  FiUploadCloud,
  FiX,
} from 'react-icons/fi';
import { format } from 'date-fns';
import { useHistory } from 'react-router-dom';

import BasePage from '../../../components/BasePage';
import { useAuth } from '../../../hooks/auth';
import { useToast } from '../../../hooks/useToast';

import 'react-datepicker/dist/react-datepicker.css';

import api from '../../../services/api';

import {
  BackLink,
  Button,
  Card,
  CardHeader,
  CategoryItem,
  CategoryList,
  DatePicker,
  EditGrid,
  EditorWrap,
  ExcerptInput,
  FormatOption,
  FormatOptions,
  InfoRow,
  InlineEditor,
  LeftColumn,
  PageContainer,
  PageHeader,
  PreviewImage,
  Sidebar,
  StatusChip,
  TitleInput,
  UploadArea,
} from './styles';

interface ICategory {
  id: string;
  title: string;
}

type NewsStatus = 'draft' | 'revision' | 'published';

const STATUS_LABEL: Record<NewsStatus, string> = {
  draft: 'Rascunho',
  revision: 'Revisão pendente',
  published: 'Publicado',
};

const STATUS_TONE: Record<NewsStatus, 'warning' | 'info' | 'success'> = {
  draft: 'warning',
  revision: 'info',
  published: 'success',
};

const CreateNewsContent: React.FC = () => {
  const { user } = useAuth();
  const { addToast } = useToast();
  const history = useHistory();

  const [categories, setCategories] = useState<ICategory[]>([]);
  const [publishedDate, setPublishedDate] = useState<Date>(new Date());
  const [immediate, setImmediate] = useState(true);

  const [featuredImgSrc, setFeaturedImgSrc] = useState('');
  const [featuredImgName, setFeaturedImgName] = useState('');
  const [audioFileName, setAudioFileName] = useState('');

  const [editingStatus, setEditingStatus] = useState(false);
  const [editingTimestamp, setEditingTimestamp] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.title = 'Criar notícia - Viva Voz - Rede Suco de Laranja';
    api
      .get<ICategory[]>('/news/categories')
      .then(response => setCategories(response.data))
      .catch(() => undefined);
  }, []);

  const handleCreatePost = useCallback(
    async (values: Record<string, unknown>) => {
      setSubmitting(true);
      try {
        const data = new FormData();
        if (values.featured_image)
          data.append('featured_image', values.featured_image as Blob);
        if (values.audio_file)
          data.append('audio_file', values.audio_file as Blob);
        data.append('document', JSON.stringify(values));

        const newPost = await api.post('/news', data, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        addToast({
          title: 'Sucesso',
          body: 'Notícia criada com sucesso.',
        });
        history.push(`/news/${newPost.data.id}`);
      } catch {
        addToast({
          title: 'Erro',
          body: 'Ocorreu algum erro ao tentar criar uma nova notícia.',
        });
      } finally {
        setSubmitting(false);
      }
    },
    [addToast, history],
  );

  return (
    <BasePage title="Nova notícia">
      <Container fluid>
        <Formik
          initialValues={{
            title: '',
            excerpt: '',
            content: '',
            status: 'draft' as NewsStatus,
            type: 'default',
            author_id: user.id,
            categories: [] as string[],
            featured_image: '' as string | Blob,
            audio_file: '' as string | Blob,
            publishedAt: publishedDate,
            syndicate_id: user.syndicate_id,
          }}
          onSubmit={handleCreatePost}
        >
          {({ values, handleSubmit, handleChange, setFieldValue }) => {
            const currentStatus = values.status as NewsStatus;
            const publishLabel = immediate
              ? 'Imediatamente'
              : format(
                  values.publishedAt as Date,
                  "dd/MM/yyyy 'às' HH:mm",
                );

            const toggleCategory = (id: string) => {
              const current = values.categories || [];
              const next = current.includes(id)
                ? current.filter(c => c !== id)
                : [...current, id];
              setFieldValue('categories', next);
            };

            return (
              <PageContainer>
                <div>
                  <BackLink type="button" onClick={() => history.push('/news')}>
                    <FiArrowLeft size={14} />
                    Voltar para notícias
                  </BackLink>

                  <PageHeader>
                    <div className="titles">
                      <h1>Nova notícia</h1>
                      <span className="subtitle">
                        Escreva o conteúdo, defina categorias, imagem de
                        destaque e quando deve ser publicada.
                      </span>
                    </div>

                    <div className="actions">
                      <Button
                        $variant="secondary"
                        type="button"
                        onClick={() => {
                          setFieldValue('status', 'draft');
                          handleSubmit();
                        }}
                        disabled={submitting}
                      >
                        <FiSave size={13} />
                        Salvar rascunho
                      </Button>

                      {user.role !== 'admin' ? (
                        <Button
                          type="button"
                          onClick={() => {
                            setFieldValue('status', 'revision');
                            handleSubmit();
                          }}
                          disabled={submitting}
                        >
                          <FiSend size={13} />
                          {submitting ? 'Enviando...' : 'Enviar para revisão'}
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          onClick={() => {
                            setFieldValue('status', 'published');
                            handleSubmit();
                          }}
                          disabled={submitting}
                        >
                          <FiCheckCircle size={13} />
                          {submitting ? 'Publicando...' : 'Publicar'}
                        </Button>
                      )}
                    </div>
                  </PageHeader>
                </div>

                <EditGrid>
                  {/* ── Main content ── */}
                  <LeftColumn>
                    <Card>
                      <TitleInput
                        type="text"
                        placeholder="Digite o título aqui..."
                        value={values.title}
                        onChange={handleChange('title')}
                      />
                      <ExcerptInput
                        placeholder="Resumo ou descrição breve da notícia (opcional)"
                        value={values.excerpt}
                        onChange={handleChange('excerpt')}
                        rows={2}
                      />
                    </Card>

                    <Card>
                      <CardHeader>
                        <div>
                          <h3>Conteúdo</h3>
                          <span className="subtitle">
                            Use o editor para formatar o texto, inserir imagens
                            e mídia.
                          </span>
                        </div>
                      </CardHeader>
                      <EditorWrap>
                        <Editor
                          apiKey={process.env.REACT_APP_TINY_API_KEY}
                          id="content-editor"
                          value={values.content}
                          onEditorChange={handleChange('content')}
                          init={{
                            height: 620,
                            menubar: false,
                            plugins: [
                              'advlist autolink lists link image imagetools charmap print preview anchor',
                              'searchreplace visualblocks code fullscreen',
                              'insertdatetime media table paste code help wordcount',
                            ],
                            toolbar:
                              'undo redo | formatselect | bold italic underline | ' +
                              'alignleft aligncenter alignright alignjustify | ' +
                              'bullist numlist outdent indent | link image media | ' +
                              'removeformat code preview fullscreen',
                            content_style:
                              "body { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 14px; color: #0f172a; }",
                          }}
                        />
                      </EditorWrap>
                    </Card>
                  </LeftColumn>

                  {/* ── Sidebar ── */}
                  <Sidebar>
                    <Card>
                      <CardHeader>
                        <div>
                          <h3>Publicação</h3>
                          <span className="subtitle">Status e agendamento</span>
                        </div>
                        <StatusChip $tone={STATUS_TONE[currentStatus]}>
                          <span className="dot" />
                          {STATUS_LABEL[currentStatus]}
                        </StatusChip>
                      </CardHeader>

                      <InfoRow>
                        <span className="icon">
                          <FiInfo size={14} />
                        </span>
                        <div className="body">
                          <span className="label">Status</span>
                          <span className="value">
                            {STATUS_LABEL[currentStatus]}
                          </span>
                        </div>
                        {!editingStatus && (
                          <button
                            type="button"
                            className="link-action"
                            onClick={() => setEditingStatus(true)}
                          >
                            Editar
                          </button>
                        )}
                      </InfoRow>

                      {editingStatus && (
                        <InlineEditor>
                          <select
                            value={values.status}
                            onChange={e => setFieldValue('status', e.target.value)}
                          >
                            <option value="draft">Rascunho</option>
                            <option value="revision">Revisão pendente</option>
                            {user.role === 'admin' && (
                              <option value="published">Publicado</option>
                            )}
                          </select>
                          <Button
                            type="button"
                            $size="sm"
                            onClick={() => setEditingStatus(false)}
                          >
                            Ok
                          </Button>
                        </InlineEditor>
                      )}

                      <InfoRow>
                        <span className="icon">
                          <FiCalendar size={14} />
                        </span>
                        <div className="body">
                          <span className="label">Publicação</span>
                          <span className="value" title={publishLabel}>
                            {publishLabel}
                          </span>
                        </div>
                        {!editingTimestamp && (
                          <button
                            type="button"
                            className="link-action"
                            onClick={() => setEditingTimestamp(true)}
                          >
                            Editar
                          </button>
                        )}
                      </InfoRow>

                      {editingTimestamp && (
                        <InlineEditor>
                          <DatePicker
                            selected={publishedDate}
                            onChange={(date: Date | null) => {
                              if (!date) return;
                              setPublishedDate(date);
                              setFieldValue('publishedAt', date);
                              setImmediate(false);
                            }}
                            locale="pt-BR"
                            timeInputLabel="Hora:"
                            dateFormat="dd/MM/yyyy HH:mm"
                            showTimeInput
                          />
                          <Button
                            type="button"
                            $size="sm"
                            onClick={() => setEditingTimestamp(false)}
                          >
                            Ok
                          </Button>
                        </InlineEditor>
                      )}
                    </Card>

                    <Card>
                      <CardHeader>
                        <div>
                          <h3>Formato</h3>
                          <span className="subtitle">Tipo de publicação</span>
                        </div>
                      </CardHeader>
                      <FormatOptions>
                        <FormatOption $active={values.type === 'default'}>
                          <input
                            type="radio"
                            name="type"
                            value="default"
                            checked={values.type === 'default'}
                            onChange={handleChange('type')}
                          />
                          <span className="label">
                            <FiFileText size={14} color="#006633" />
                            Padrão (texto)
                          </span>
                        </FormatOption>
                      </FormatOptions>
                    </Card>

                    <Card>
                      <CardHeader>
                        <div>
                          <h3>Categorias</h3>
                          <span className="subtitle">
                            {values.categories.length}
                            {' '}
                            selecionadas
                          </span>
                        </div>
                        <FiTag size={14} color="#64748b" />
                      </CardHeader>

                      {categories.length === 0 ? (
                        <span
                          style={{
                            fontSize: 12,
                            color: '#94a3b8',
                            fontWeight: 500,
                            fontStyle: 'italic',
                          }}
                        >
                          Nenhuma categoria disponível.
                        </span>
                      ) : (
                        <CategoryList>
                          {categories.map(category => {
                            const checked = values.categories.includes(
                              category.id,
                            );
                            return (
                              <CategoryItem
                                key={category.id}
                                $checked={checked}
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => toggleCategory(category.id)}
                                />
                                <span className="text">{category.title}</span>
                              </CategoryItem>
                            );
                          })}
                        </CategoryList>
                      )}
                    </Card>

                    <Card>
                      <CardHeader>
                        <div>
                          <h3>Imagem destacada</h3>
                          <span className="subtitle">
                            Exibida na listagem e no topo
                          </span>
                        </div>
                      </CardHeader>

                      {featuredImgSrc ? (
                        <PreviewImage>
                          <img src={featuredImgSrc} alt={featuredImgName} />
                          <button
                            type="button"
                            className="remove"
                            aria-label="Remover imagem"
                            onClick={() => {
                              setFeaturedImgSrc('');
                              setFeaturedImgName('');
                              setFieldValue('featured_image', '');
                            }}
                          >
                            <FiX size={14} />
                          </button>
                        </PreviewImage>
                      ) : (
                        <UploadArea $hasFile={false}>
                          <span className="icon">
                            <FiImage size={18} />
                          </span>
                          <span className="title">Enviar imagem destacada</span>
                          <span className="hint">PNG, JPG — máx. 5MB</span>
                          <input
                            type="file"
                            accept="image/png, image/jpeg, image/jpg"
                            onChange={e => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              setFeaturedImgSrc(URL.createObjectURL(file));
                              setFeaturedImgName(file.name);
                              setFieldValue('featured_image', file);
                            }}
                          />
                        </UploadArea>
                      )}

                      {featuredImgSrc && (
                        <UploadArea $hasFile>
                          <span className="icon">
                            <FiUploadCloud size={16} />
                          </span>
                          <span className="file-name" title={featuredImgName}>
                            Trocar imagem
                          </span>
                          <input
                            type="file"
                            accept="image/png, image/jpeg, image/jpg"
                            onChange={e => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              setFeaturedImgSrc(URL.createObjectURL(file));
                              setFeaturedImgName(file.name);
                              setFieldValue('featured_image', file);
                            }}
                          />
                        </UploadArea>
                      )}
                    </Card>

                    <Card>
                      <CardHeader>
                        <div>
                          <h3>Áudio</h3>
                          <span className="subtitle">
                            Anexe uma gravação em áudio
                          </span>
                        </div>
                      </CardHeader>

                      <UploadArea $hasFile={!!audioFileName}>
                        <span className="icon">
                          <FiMic size={16} />
                        </span>
                        {audioFileName ? (
                          <>
                            <span className="file-name" title={audioFileName}>
                              {audioFileName}
                            </span>
                            <span className="hint">Clique para trocar</span>
                          </>
                        ) : (
                          <>
                            <span className="title">
                              Selecionar arquivo de áudio
                            </span>
                            <span className="hint">MP3, WAV, M4A</span>
                          </>
                        )}
                        <input
                          type="file"
                          accept="audio/*"
                          onChange={e => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            setAudioFileName(file.name);
                            setFieldValue('audio_file', file);
                          }}
                        />
                      </UploadArea>
                    </Card>
                  </Sidebar>
                </EditGrid>
              </PageContainer>
            );
          }}
        </Formik>
      </Container>
    </BasePage>
  );
};

export default CreateNewsContent;
